import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import { isNil } from 'ramda';
import { Icon, TIconType } from '../Icon';
import { Text } from '../Text';
import { TextAndIcon } from '../TextAndIcon';

/***** STYLES *****/
import * as styles from './Button.css';

/***** TYPES *****/
import { TColors } from '../../types/colors';

export type TButtonTypes = 'primary' | 'secondary' | 'plain' | 'light';

export interface IButtonProps extends React.HTMLProps<HTMLButtonElement> {
  type?: TButtonTypes;
  htmlType?: string;
  sync?: boolean;
  reverse?: boolean;
  icon?: TIconType;
  iconColor?: TColors;
  iconColorHovered?: TColors;
  iconSize?: 7 | 10 | 16;
  fluid?: 'true';
  dataMark?: string;
  normalText?: boolean;
}

type TMixTypeRest = Pick<IButtonProps, 'fluid' | 'className' | 'sync' | 'disabled'>;

interface IColorsType {
  default: TColors;
  hover: TColors;
}
const ColorsMap: {
  [key: string]: IColorsType,
} = {
  disabled: {
    default: 'gray_2',
    hover: 'gray_2',
  },
  light: {
    default: 'blue',
    hover: 'blue_hover',
  },
  plain: {
    default: 'blue',
    hover: 'blue_hover',
  },
  primary: {
    default: 'white',
    hover: 'white',
  },
  secondary: {
    default: 'blue',
    hover: 'blue_hover',
  },
};

export type TButtonTypeTypes = 'button' | 'submit' | 'reset' | undefined;

/***** COMPONENT *****/
/**
 * Кнопка https://zpl.io/29ZozJW
 * Кнопка с иконкой и текстом. Поддерживает прелоадер.
 */
export class Button extends React.PureComponent<IButtonProps> {
  public render() {
    const {
      icon, iconSize = 16, iconColor, iconColorHovered, type = 'plain', sync, htmlType = 'button',
      reverse, children, normalText, className, dataMark = 'Button', ...rest } = this.props;
    const isOnlyIcon = React.Children.count(children) === 0 && !isNil(icon);
    const color = rest.disabled ? ColorsMap.disabled : ColorsMap[type];

    return (
      <button
        data-mark={dataMark}
        {...this.mixTypesStyles(type, isOnlyIcon, { ...this.props, className })}
        {...rest} type={htmlType as TButtonTypeTypes}
      >
        <span className={styles['icon_text']}>
          {this.textAndIcon(color, children, icon, reverse, normalText)}
        </span>
        <span className={styles['loader']}>
        {
          sync && <Icon type="loader" color={color.default} size={iconSize}/>
        }
        </span>
      </button>
    );
  }
  private textAndIcon = (
    color: IColorsType,
    children?: React.ReactNode,
    icon?: TIconType,
    reverse?: boolean,
    normalText?: boolean,
  ) => {
    const { iconColor, iconColorHovered } = this.props;

    if (icon && React.Children.count(children)) {
      return (
        <TextAndIcon
          iconType={icon}
          iconColor={iconColor || color.default}
          iconColorHovered={iconColorHovered || color.hover}
          reverse={!reverse}
          fontWeight={normalText ? 'normal' : 'bold'}
          iconSize={this.props.iconSize}
          whiteSpace="nowrap"
        >
          {children}
        </TextAndIcon>
      );
    } else if (icon) {
      return <Icon
        type={icon}
        color={iconColor || color.default}
        colorHovered={iconColorHovered || color.hover}
        size={this.props.iconSize}
      />;
    }

    return <Text fontWeight={normalText ? 'normal' : 'bold'} whiteSpace="nowrap">{children}</Text>;
  }
  private mixTypesStyles = (type: TButtonTypes, isOnlyIcon?: boolean, rest?: TMixTypeRest) => mergeStyles([
    styles['button'],
    styles[type],
    isOnlyIcon && styles['only_icon_button'],
    rest && rest.fluid && styles['fluid'],
    rest && rest.className,
    rest && rest.sync && styles['sync'],
    rest && rest.disabled && styles['disabled'],
  ])
}
